import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../api';

// COD money only becomes real on delivery, so this page is ordered by risk,
// not by date: anything that turned around sits at the top, then high-value
// parcels that have gone quiet, then everything else.

const STATUS = {
  delivered:        { text: 'Delivered',       bg: '#e8f3ec', fg: '#2e7d50', border: '#2e7d50' },
  out_for_delivery: { text: 'Out for delivery', bg: '#e9eef3', fg: '#2a6f8a', border: '#2a6f8a' },
  in_transit:       { text: 'In transit',      bg: '#ffffff', fg: '#4a3840', border: '#e6dccc' },
  rto:              { text: 'Coming back (RTO)', bg: '#f3cfcb', fg: '#8c1d18', border: '#8c1d18' },
  rto_delivered:    { text: 'Returned to us',  bg: '#f3cfcb', fg: '#8c1d18', border: '#8c1d18' },
  unknown:          { text: 'No scan yet',     bg: '#ffffff', fg: '#7d6a63', border: '#e6dccc' }
};

const money = (n) => '₹' + (n || 0).toLocaleString('en-IN');

function Badge({ status }) {
  const s = STATUS[status] || STATUS.unknown;
  return (
    <span style={{
      fontSize: 11, padding: '2px 8px', borderRadius: 999,
      background: s.bg, color: s.fg, border: `1px solid ${s.border}`, whiteSpace: 'nowrap'
    }}>
      {s.text}
    </span>
  );
}

function Tile({ label, value, tone }) {
  const fg = tone === 'bad' ? '#b3261e' : tone === 'warn' ? '#7a6a12' : '#2e1f23';
  return (
    <div style={{
      background: '#f8f3eb', border: '1px solid #e6dccc', borderRadius: 8,
      padding: '12px 16px', minWidth: 150
    }}>
      <div style={{ fontSize: 11, color: '#7d6a63', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 20, color: fg }}>{value}</div>
    </div>
  );
}

export default function CodTracking() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('attention');
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/cod/tracking`);
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (loading) return <div style={{ color: '#7d6a63' }}>Loading COD parcels…</div>;
  if (error) return <div style={{ color: '#b3261e' }}>Could not load: {error}</div>;
  if (!data) return null;

  const { summary, rows, highValueThreshold, staleDays } = data;

  const q = search.trim().toLowerCase();
  const visible = rows.filter(r => {
    if (filter === 'attention' && !r.needsAttention) return false;
    if (filter === 'inflight' && (r.delivered || r.failed)) return false;
    if (filter === 'failed' && !r.failed) return false;
    if (filter === 'highvalue' && !r.highValue) return false;
    if (!q) return true;
    return (
      String(r.orderNumber || '').toLowerCase().includes(q) ||
      String(r.customerName || '').toLowerCase().includes(q) ||
      String(r.waybill || '').toLowerCase().includes(q)
    );
  });

  const tabs = [
    ['attention', `Needs action (${summary.needsAttention})`],
    ['failed', `Failed (${summary.failed})`],
    ['inflight', `In flight (${summary.inFlight})`],
    ['highvalue', `Over ${money(highValueThreshold)}`],
    ['all', `All (${summary.total})`]
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 14 }}>
        <h2 style={{ margin: 0 }}>COD tracking</h2>
        <button
          onClick={load}
          style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, cursor: 'pointer', background: '#ffffff', color: '#7d6a63', border: '1px solid #e6dccc' }}
        >
          Refresh
        </button>
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        <Tile label="Cash still in flight" value={money(summary.cashInFlight)} />
        <Tile label="Needs action" value={summary.needsAttention} tone={summary.needsAttention ? 'warn' : undefined} />
        <Tile label="Failed deliveries" value={summary.failed} tone={summary.failed ? 'bad' : undefined} />
        <Tile label="Cash lost to failures" value={money(summary.cashLostToFailures)} tone={summary.cashLostToFailures ? 'bad' : undefined} />
        <Tile label="Delivered" value={summary.delivered} />
      </div>

      {summary.failed > 0 && (
        <div style={{
          background: '#fbe9e7', border: '1px solid #8c1d18', color: '#8c1d18',
          borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 14
        }}>
          ⚠️ {summary.failed} COD parcel{summary.failed > 1 ? 's have' : ' has'} failed delivery —
          {' '}{money(summary.cashLostToFailures)} never collected. These are listed first.
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        {tabs.map(([key, text]) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            style={{
              fontSize: 12, padding: '5px 11px', borderRadius: 6, cursor: 'pointer',
              background: filter === key ? '#57161f' : '#ffffff',
              color: filter === key ? '#fff' : '#7d6a63',
              border: `1px solid ${filter === key ? '#57161f' : '#e6dccc'}`
            }}
          >
            {text}
          </button>
        ))}
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search order, customer or AWB…"
          style={{
            marginLeft: 'auto', padding: '6px 10px', borderRadius: 6, fontSize: 13,
            background: '#ffffff', color: '#2e1f23', border: '1px solid #e6dccc', minWidth: 240
          }}
        />
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ color: '#7d6a63', textAlign: 'left' }}>
            <th style={{ padding: '8px 6px' }}>Order</th>
            <th style={{ padding: '8px 6px' }}>Customer</th>
            <th style={{ padding: '8px 6px', textAlign: 'right' }}>COD</th>
            <th style={{ padding: '8px 6px' }}>Status</th>
            <th style={{ padding: '8px 6px', textAlign: 'right' }}>Age</th>
            <th style={{ padding: '8px 6px' }}>AWB</th>
          </tr>
        </thead>
        <tbody>
          {visible.map(r => (
            <tr
              key={r.orderId}
              style={{
                borderTop: '1px solid #e6dccc',
                background: r.failed ? '#fbe9e7' : r.needsAttention ? '#f5f1d6' : 'transparent'
              }}
            >
              <td style={{ padding: '8px 6px', fontFamily: 'monospace' }}>{r.orderNumber}</td>
              <td style={{ padding: '8px 6px' }}>{r.customerName}</td>
              <td style={{
                padding: '8px 6px', textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                color: r.highValue ? '#7a6a12' : '#2e1f23',
                fontWeight: r.highValue ? 600 : 400
              }}>
                {money(r.codAmount)}
              </td>
              <td style={{ padding: '8px 6px' }}>
                <Badge status={r.trackingStatus} />
                {r.stalled && !r.failed && (
                  <span style={{ marginLeft: 6, fontSize: 11, color: '#7a6a12' }}>
                    quiet {staleDays}d+
                  </span>
                )}
              </td>
              <td style={{ padding: '8px 6px', textAlign: 'right', color: '#7d6a63' }}>
                {r.daysSinceManifest == null ? '—' : `${r.daysSinceManifest}d`}
              </td>
              <td style={{ padding: '8px 6px' }}>
                {r.waybill && (
                  <a
                    href={r.courier === 'Delhivery'
                      ? `https://www.delhivery.com/track/package/${r.waybill}`
                      : `https://shiprocket.co/tracking/${r.waybill}`}
                    target="_blank" rel="noreferrer"
                    style={{ color: '#8c3242', fontFamily: 'monospace', textDecoration: 'underline' }}
                  >
                    {r.waybill}
                  </a>
                )}
              </td>
            </tr>
          ))}
          {visible.length === 0 && (
            <tr>
              <td colSpan="6" style={{ padding: 20, textAlign: 'center', color: '#93827a' }}>
                {filter === 'attention'
                  ? 'Nothing needs chasing — every COD parcel is on track.'
                  : 'No COD parcels match this filter.'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
